/**
 * SoleadLab 2.0 — IV Assign Panel
 *
 * Two-pane UI for assigning IV measurements to string labels from the vector
 * site layout. Assigned strings are visualized as colored circles on the map.
 *
 * Left pane:  measurement list with checkboxes + shift-click range select
 * Right pane: string label list (natural sort), click to batch-assign
 *
 * Auto-assign modes:
 *   Sequential — measurements in order → labels in order (asc/desc)
 *   By Schema  — paste a sample marker value, detect tokens, assign topology
 *                fields to each token, then match against label hierarchy
 */

import { useState, useMemo, useCallback } from 'react';
import { useIVStore } from '@/stores/ivStore';
import { useProjectStore } from '@/stores/projectStore';
import { useMapStore } from '@/stores/mapStore';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import * as ivApi from '@/services/ivApi';
import type { SiteLayout, TopologyFields } from '@/domain/layout/types';

// ─── Natural sort ─────────────────────────────────────────────────────────────

function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

// ─── Result color ─────────────────────────────────────────────────────────────

function resultColor(pct: number | null): string {
  if (pct === null || pct === undefined) return 'var(--text-muted)';
  if (pct >= 0)  return '#22c55e';
  if (pct >= -5) return '#f97316';
  return '#ef4444';
}

/** Filter out device placeholder values like "----", "---", "" */
function notPlaceholder(val: string | null | undefined): val is string {
  if (!val) return false;
  return !/^-+$/.test(val.trim());
}

// ─── Marker segment detection ──────────────────────────────────────────────────
//
// Mirrors the logic of the label schema builder (labelSchemaCompiler.ts) but
// applied to marker values instead of site labels.  The user pastes one sample
// marker value (e.g. "2.2.11"), sees its tokens as editable chips, assigns a
// topology field to each token, and the system builds a regex from those chips
// to extract fields from every measurement.

export interface MarkerSegment {
  value: string;                          // token text, e.g. "2" or "DCCB11"
  field: keyof TopologyFields | 'skip';  // assigned topology field
  sep:   string;                          // separator that follows this token
}

const TOPO_KEYS: Array<keyof TopologyFields> = [
  'station', 'inverter', 'mppt', 'dccb', 'string', 'module',
];

/**
 * Split a sample marker value into tokens and auto-assign topology fields.
 * Pure-number tokens are assigned positionally: station, mppt, dccb, string…
 * Prefix tokens (DCCB, STR, INV, MPPT, MOD) are recognized by prefix.
 */
export function detectMarkerTokens(sample: string): MarkerSegment[] {
  const result: MarkerSegment[] = [];
  const re = /([A-Za-z0-9]+)([^A-Za-z0-9]*)/g;
  let numIdx = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(sample)) !== null) {
    const token = match[1];
    const sep   = match[2] ?? '';

    let field: keyof TopologyFields | 'skip' = 'skip';
    if      (/^DCCB/i.test(token) || /^CB\d/i.test(token))  field = 'dccb';
    else if (/^STR/i.test(token))                             field = 'string';
    else if (/^INV/i.test(token))                             field = 'inverter';
    else if (/^MPPT/i.test(token))                            field = 'mppt';
    else if (/^MOD/i.test(token))                             field = 'module';
    else if (/^STA/i.test(token) || /^TR\d/i.test(token))    field = 'station';
    else if (/^\d+$/.test(token)) {
      const posMap: Array<keyof TopologyFields> = ['station', 'mppt', 'dccb', 'string', 'module'];
      field = posMap[numIdx] ?? 'skip';
      numIdx++;
    }

    result.push({ value: token, field, sep });
  }
  return result;
}

/**
 * Extract topology fields from a marker value using segment definitions.
 * Builds a capturing regex where:
 *   - pure-number tokens → (\d+)
 *   - prefixed tokens    → (PREFIX\d+)   (e.g. DCCB → (DCCB\d+))
 *   - 'skip' tokens      → non-capturing equivalent
 */
export function parseWithSegments(
  segments: MarkerSegment[],
  value: string | null | undefined,
): Partial<TopologyFields> {
  if (!segments.length || !value?.trim()) return {};

  let regexStr = '^';
  const fieldNames: (keyof TopologyFields)[] = [];

  for (const seg of segments) {
    const prefix    = seg.value.match(/^([A-Za-z]+)/)?.[1];
    const isNumeric = /^\d+$/.test(seg.value);

    if (seg.field === 'skip') {
      regexStr += isNumeric ? '\\d+' : '[A-Za-z0-9]+';
    } else {
      fieldNames.push(seg.field);
      regexStr += prefix ? `(${prefix}\\d+)` : '(\\d+)';
    }

    // Escape separator literal
    if (seg.sep) regexStr += seg.sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  regexStr += '$';

  try {
    const re = new RegExp(regexStr, 'i');
    const m  = value.trim().match(re);
    if (!m) return {};
    const result: Partial<TopologyFields> = {};
    fieldNames.forEach((f, i) => { result[f] = m[i + 1]; });
    return result;
  } catch {
    return {};
  }
}

/** Strip non-numeric prefix/suffix for numeric comparison: "DCCB11" == "11" */
function toNum(val: string): number | null {
  const m = val.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

/** Check if extracted measurement fields match a label's hierarchy */
function hierarchyMatch(
  mFields:     Partial<TopologyFields>,
  lHierarchy:  Partial<TopologyFields> | undefined,
): boolean {
  if (!lHierarchy) return false;
  const entries = Object.entries(mFields) as Array<[keyof TopologyFields, string | undefined]>;
  if (entries.length === 0) return false;
  for (const [key, mVal] of entries) {
    if (!mVal) continue;
    const lVal = lHierarchy[key];
    if (lVal === undefined) return false;
    const mn = toNum(mVal), ln = toNum(lVal);
    if (mn !== null && ln !== null) {
      if (mn !== ln) return false;
    } else if (mVal.toLowerCase() !== lVal.toLowerCase()) return false;
  }
  return true;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type AutoMode = 'sequential' | 'schema';

/**
 * F-01D-2: a single assignable string label, scoped to its owning layout.
 *
 * Two layouts can contain the same `value` (e.g. both call their first
 * string "STR-1"); each gets its own option carrying its own `layoutId`
 * and `layoutName` so the user can distinguish them in the UI and the
 * payload sent to the backend can pin the correct one.
 *
 * `key` is unique across layouts (`<layoutId>::<entityId>`) and is used as
 * the React list key.
 */
export interface IvLabelOption {
  key: string;
  entityId: string;
  value: string;
  hierarchy: Partial<TopologyFields> | undefined;
  layoutId: string;
  layoutName: string;
}

interface SchemaMatchResult {
  id:           string;
  marker1:      string;                  // m.label — first marker field
  marker2:      string | null;
  testNum:      number | null;           // measurement order (display only)
  parsedFields: Partial<TopologyFields>;
  matchedLabel: string | null;
  matchedLayoutId: string | null;
}

interface Props {
  sessionId: string;
}

/**
 * F-01D-2: resolve the effective list of vector layouts available for IV
 * assignment. Prefers the canonical `data.siteLayouts[]`; falls back to a
 * length-1 wrap of the legacy singleton when no array is present.
 *
 * Exported for unit testing. Accepts a minimal `{ siteLayouts?, siteLayout? }`
 * shape so callers can pass narrow Zustand selectors (per the project rule
 * against `useStore(s => s.data)` broad selectors that re-render on every
 * unrelated data mutation).
 */
export function getLayoutsForIvAssignment(
  data: { siteLayouts?: (SiteLayout | undefined)[]; siteLayout?: SiteLayout | null },
): SiteLayout[] {
  const arr = data.siteLayouts && data.siteLayouts.length > 0
    ? data.siteLayouts
    : (data.siteLayout ? [data.siteLayout] : []);
  return arr.filter((l): l is SiteLayout => !!l);
}

/**
 * F-01D-2: flatten string-level label options across every layout into a
 * single, naturally-sorted list. Each option carries its owning
 * `layoutId` + `layoutName` so the UI can show the layout name as a
 * subtitle/suffix and the assignment payload can pin the correct layout.
 *
 * Duplicate `value`s from different layouts produce SEPARATE entries —
 * they are distinct assignable targets.
 *
 * Exported for unit testing.
 */
export function buildIvLabelOptions(layouts: SiteLayout[]): IvLabelOption[] {
  const out: IvLabelOption[] = [];
  for (const layout of layouts) {
    if (!layout.textEntities) continue;
    // Dedupe by `value` WITHIN a single layout. If the same logical string is
    // printed in multiple text entities of the same layout (e.g. the PDF has a
    // primary label + a secondary annotation), they represent the SAME
    // assignable target and should produce one option. The first entity wins
    // (stable). Duplicates ACROSS layouts are preserved — see test.
    const seenValuesInLayout = new Set<string>();
    for (const entity of layout.textEntities) {
      const cls = entity.classificationOverride ?? entity.classification;
      if (cls?.level !== 'string' || !cls.value) continue;
      if (seenValuesInLayout.has(cls.value)) continue;
      seenValuesInLayout.add(cls.value);
      out.push({
        key: `${layout.id}::${entity.id}`,
        entityId: entity.id,
        value: cls.value,
        hierarchy: cls.hierarchy,
        layoutId: layout.id,
        layoutName: layout.name || 'Untitled layout',
      });
    }
  }
  // Sort naturally by value; ties broken by layoutName so duplicate values
  // group together with deterministic layout order.
  out.sort((a, b) => {
    const v = naturalCompare(a.value, b.value);
    if (v !== 0) return v;
    return naturalCompare(a.layoutName, b.layoutName);
  });
  return out;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function IVAssignPanel({ sessionId }: Props) {
  // F-02E-2B: the whole IV assignment workflow (assign / unassign /
  // auto-assign / schema preview) is mutation. Deep IV reads
  // (measurements, findings) are also NOT shareReadable today per the
  // F-02E-2 diagnosis — backend would 403 those. We compute the
  // read-only flag first, run all other hooks unconditionally to keep
  // hook order stable across renders, then short-circuit the render.
  const isReadOnly = useIsReadOnlyViewer();
  const measurements      = useIVStore((s) => s.measurements);
  const fetchMeasurements = useIVStore((s) => s.fetchMeasurements);
  // F-01D-2: aggregate labels across all layouts instead of binding to the
  // singleton alias. Use NARROW selectors (per project rule in CLAUDE.md —
  // `useStore(s => s.data)` would re-render on every unrelated data mutation
  // and was causing click-storms after the first assignment).
  const siteLayouts       = useProjectStore((s) => s.data.siteLayouts);
  const siteLayout        = useProjectStore((s) => s.data.siteLayout);

  const [selected, setSelected]         = useState<Set<string>>(new Set());
  const [lastClickIdx, setLastClickIdx] = useState<number | null>(null);
  const [assigning, setAssigning]       = useState(false);
  const [labelFilter, setLabelFilter]   = useState('');
  const [error, setError]               = useState('');

  // Auto-assign config
  const [autoOpen, setAutoOpen]   = useState(false);
  const [autoMode, setAutoMode]   = useState<AutoMode>('sequential');

  // Sequential mode
  const [mOrder, setMOrder]             = useState<'asc' | 'desc'>('asc');
  const [lOrder, setLOrder]             = useState<'asc' | 'desc'>('asc');
  const [skipAssigned, setSkipAssigned] = useState(true);

  // Schema mode — sample input + detected token segments (all three markers)
  const [m1Sample, setM1Sample]         = useState('');
  const [m2Sample, setM2Sample]         = useState('');
  const [m3Sample, setM3Sample]         = useState('');
  const [m1Segments, setM1Segments]     = useState<MarkerSegment[]>([]);
  const [m2Segments, setM2Segments]     = useState<MarkerSegment[]>([]);
  const [m3Segments, setM3Segments]     = useState<MarkerSegment[]>([]);
  const [schemaPreview, setSchemaPreview] = useState<SchemaMatchResult[] | null>(null);

  // ── Derived data ───────────────────────────────────────────────────────────

  const sortedMeasurements = useMemo(() => {
    return [...measurements].sort((a, b) => {
      const ta = Number(a.extras?.test_number ?? NaN);
      const tb = Number(b.extras?.test_number ?? NaN);
      if (!isNaN(ta) && !isNaN(tb)) return ta - tb;
      return (a.source_row_index ?? 0) - (b.source_row_index ?? 0);
    });
  }, [measurements]);

  // F-01D-2: stringLabels is now a flat IvLabelOption[] aggregated across
  // every layout. Duplicate `value`s from different layouts are kept as
  // separate options keyed by `<layoutId>::<entityId>`.
  const stringLabels = useMemo(
    () => buildIvLabelOptions(getLayoutsForIvAssignment({ siteLayouts, siteLayout })),
    [siteLayouts, siteLayout],
  );

  // F-01D-2: only the label `value`s that actually appear in 2+ layouts need
  // the layout-name disambiguator subtitle. Showing it on every row would
  // repeat the same layout name once per label — visual noise that the
  // product owner correctly read as "duplicate layouts" during smoke.
  const collidingValues = useMemo(() => {
    const counts = new Map<string, Set<string>>();
    for (const l of stringLabels) {
      const set = counts.get(l.value) ?? new Set<string>();
      set.add(l.layoutId);
      counts.set(l.value, set);
    }
    const out = new Set<string>();
    counts.forEach((layoutIds, value) => {
      if (layoutIds.size > 1) out.add(value);
    });
    return out;
  }, [stringLabels]);

  const filteredLabels = useMemo(() => {
    const q = labelFilter.trim().toLowerCase();
    if (!q) return stringLabels;
    return stringLabels.filter((l) =>
      l.value.toLowerCase().includes(q) || l.layoutName.toLowerCase().includes(q),
    );
  }, [stringLabels, labelFilter]);

  // F-01D-2: assignment lookup is keyed by the composite (layoutId, mapLabel)
  // so the same label value across two layouts has independent counts.
  // Legacy rows where measurement.layout_id is null fall under the bucket
  // `'__legacy__:<map_label>'` so we still mark labels-with-legacy-assignments
  // as taken without conflating layouts.
  const labelAssignments = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of measurements) {
      if (!m.map_label) continue;
      const key = `${m.layout_id ?? '__legacy__'}::${m.map_label}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m.id);
    }
    return map;
  }, [measurements]);

  /**
   * Helper: return the assignment-bucket key for an option, AND also check the
   * legacy bucket. Legacy measurements (layout_id IS NULL) should still mark a
   * matching-by-value label as "assigned" so the user doesn't double-assign.
   */
  const getAssignmentIds = useCallback(
    (opt: IvLabelOption): string[] => {
      const exact = labelAssignments.get(`${opt.layoutId}::${opt.value}`) ?? [];
      const legacy = labelAssignments.get(`__legacy__::${opt.value}`) ?? [];
      // Concatenate without dedup (an id can't appear in both buckets — a
      // single measurement has a single layout_id state).
      return exact.length > 0 || legacy.length > 0 ? [...exact, ...legacy] : [];
    },
    [labelAssignments],
  );

  const labelResultPct = useMemo(() => {
    const result = new Map<string, number | null>();
    for (const m of measurements) {
      if (!m.map_label) continue;
      const key = `${m.layout_id ?? '__legacy__'}::${m.map_label}`;
      const existing = result.get(key);
      if (existing === undefined || (m.result_pct !== null && (existing === null || m.result_pct < existing))) {
        result.set(key, m.result_pct);
      }
    }
    return result;
  }, [measurements]);

  const getResultPct = useCallback(
    (opt: IvLabelOption): number | null => {
      const exact = labelResultPct.get(`${opt.layoutId}::${opt.value}`);
      if (exact !== undefined) return exact;
      const legacy = labelResultPct.get(`__legacy__::${opt.value}`);
      return legacy ?? null;
    },
    [labelResultPct],
  );

  // First non-placeholder marker values (hints shown in schema config)
  const m1Hint = useMemo(
    () => sortedMeasurements.find((m) => notPlaceholder(m.label))?.label ?? null,
    [sortedMeasurements],
  );
  const m2Hint = useMemo(
    () => sortedMeasurements.find((m) => notPlaceholder(m.marker2))?.marker2 ?? null,
    [sortedMeasurements],
  );
  const m3Hint = useMemo(
    () => sortedMeasurements.find((m) => notPlaceholder(m.marker3))?.marker3 ?? null,
    [sortedMeasurements],
  );

  // ── Sequential auto-assign preview ────────────────────────────────────────

  const seqPreview = useMemo(() => {
    const ms = mOrder === 'asc' ? sortedMeasurements : [...sortedMeasurements].reverse();
    const ls = lOrder === 'asc' ? stringLabels : [...stringLabels].reverse();
    const filtMs = skipAssigned ? ms.filter((m) => !m.map_label) : ms;
    // F-01D-2: "label is taken" = it has any assignment (exact-layout OR
    // legacy NULL-layout) so sequential auto-assign doesn't try to reuse it.
    const filtLs = skipAssigned ? ls.filter((l) => getAssignmentIds(l).length === 0) : ls;
    return Math.min(filtMs.length, filtLs.length);
  }, [mOrder, lOrder, skipAssigned, sortedMeasurements, stringLabels, getAssignmentIds]);

  // ── Schema match computation ───────────────────────────────────────────────

  const computeSchemaMatches = useCallback((): SchemaMatchResult[] => {
    const results: SchemaMatchResult[] = [];
    // F-01D-2: "used" tracks the same composite key the assignment-payload
    // builder uses below, so two distinct layouts with the same string value
    // can both still match exactly once each.
    const usedKeys = new Set<string>();

    for (const m of sortedMeasurements) {
      const fields: Partial<TopologyFields> = {
        ...parseWithSegments(m1Segments, m.label),
        ...parseWithSegments(m2Segments, m.marker2),
        ...parseWithSegments(m3Segments, m.marker3),
      };

      let matchedLabel: string | null = null;
      let matchedLayoutId: string | null = null;
      if (Object.keys(fields).length > 0) {
        for (const lbl of stringLabels) {
          const key = `${lbl.layoutId}::${lbl.value}`;
          if (usedKeys.has(key)) continue;
          if (hierarchyMatch(fields, lbl.hierarchy)) {
            matchedLabel = lbl.value;
            matchedLayoutId = lbl.layoutId;
            usedKeys.add(key);
            break;
          }
        }
      }

      const testNum = m.extras?.test_number != null ? Number(m.extras.test_number) : null;
      results.push({
        id:           m.id,
        marker1:      m.label,
        marker2:      m.marker2,
        testNum:      !isNaN(testNum as number) ? testNum : null,
        parsedFields: fields,
        matchedLabel,
        matchedLayoutId,
      });
    }
    return results;
  }, [sortedMeasurements, stringLabels, m1Segments, m2Segments, m3Segments]);

  const schemaMatchCount = useMemo(() => {
    if (!autoOpen || autoMode !== 'schema') return 0;
    if (m1Segments.length === 0 && m2Segments.length === 0 && m3Segments.length === 0) return 0;
    const usedKeys = new Set<string>();
    let count = 0;
    for (const m of sortedMeasurements) {
      const fields: Partial<TopologyFields> = {
        ...parseWithSegments(m1Segments, m.label),
        ...parseWithSegments(m2Segments, m.marker2),
        ...parseWithSegments(m3Segments, m.marker3),
      };
      if (Object.keys(fields).length === 0) continue;
      for (const lbl of stringLabels) {
        const key = `${lbl.layoutId}::${lbl.value}`;
        if (usedKeys.has(key)) continue;
        if (hierarchyMatch(fields, lbl.hierarchy)) {
          usedKeys.add(key);
          count++;
          break;
        }
      }
    }
    return count;
  }, [autoOpen, autoMode, sortedMeasurements, stringLabels, m1Segments, m2Segments, m3Segments]);

  // ── Checkbox toggle ────────────────────────────────────────────────────────

  const handleCheck = useCallback(
    (id: string, idx: number, shiftKey: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (shiftKey && lastClickIdx !== null) {
          const lo = Math.min(idx, lastClickIdx);
          const hi = Math.max(idx, lastClickIdx);
          for (const m of sortedMeasurements.slice(lo, hi + 1)) next.add(m.id);
        } else {
          if (next.has(id)) next.delete(id);
          else next.add(id);
        }
        return next;
      });
      setLastClickIdx(idx);
    },
    [lastClickIdx, sortedMeasurements],
  );

  const handleSelectAll = () => {
    if (selected.size === sortedMeasurements.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedMeasurements.map((m) => m.id)));
    }
  };

  // ── Manual label click assign ──────────────────────────────────────────────

  const handleLabelClick = useCallback(
    async (_labelValue: string, labelIdx: number) => {
      if (selected.size === 0 || assigning) return;
      setError('');
      const selectedMs     = sortedMeasurements.filter((m) => selected.has(m.id));
      const labelsToAssign = filteredLabels.slice(labelIdx, labelIdx + selectedMs.length);
      if (labelsToAssign.length === 0) return;
      // F-01D-2: each assignment pins both map_label AND layout_id so the
      // backend can disambiguate identical labels across multiple layouts.
      const assignments = selectedMs.slice(0, labelsToAssign.length).map((m, i) => ({
        id: m.id,
        map_label: labelsToAssign[i].value,
        layout_id: labelsToAssign[i].layoutId,
      }));
      setAssigning(true);
      try {
        await ivApi.bulkAssignMapLabels(sessionId, assignments);
        await fetchMeasurements(sessionId);
        setSelected(new Set());
      } catch (err) {
        setError(String(err));
      } finally {
        setAssigning(false);
      }
    },
    [selected, assigning, sortedMeasurements, filteredLabels, sessionId, fetchMeasurements],
  );

  // ── Sequential auto-assign ────────────────────────────────────────────────

  const handleSeqAutoAssign = async () => {
    if (assigning) return;
    setError('');
    const ms      = mOrder === 'asc' ? sortedMeasurements : [...sortedMeasurements].reverse();
    const ls      = lOrder === 'asc' ? stringLabels : [...stringLabels].reverse();
    const filtMs  = skipAssigned ? ms.filter((m) => !m.map_label) : ms;
    const filtLs  = skipAssigned ? ls.filter((l) => getAssignmentIds(l).length === 0) : ls;
    const count   = Math.min(filtMs.length, filtLs.length);
    if (count === 0) return;
    // F-01D-2: include layout_id from each label option.
    const assignments = filtMs.slice(0, count).map((m, i) => ({
      id: m.id,
      map_label: filtLs[i].value,
      layout_id: filtLs[i].layoutId,
    }));
    setAssigning(true);
    try {
      await ivApi.bulkAssignMapLabels(sessionId, assignments);
      await fetchMeasurements(sessionId);
      setAutoOpen(false);
    } catch (err) {
      setError(String(err));
    } finally {
      setAssigning(false);
    }
  };

  // ── Schema auto-assign ────────────────────────────────────────────────────

  const handleSchemaPreview = () => setSchemaPreview(computeSchemaMatches());

  const handleSchemaRun = async () => {
    if (assigning) return;
    setError('');
    const matches     = schemaPreview ?? computeSchemaMatches();
    // F-01D-2: schema-mode assignments carry the matched layout id so
    // duplicate string values across layouts are split into separate batches.
    const assignments = matches
      .filter((r) => r.matchedLabel !== null && r.matchedLayoutId !== null)
      .map((r) => ({
        id: r.id,
        map_label: r.matchedLabel!,
        layout_id: r.matchedLayoutId!,
      }));
    if (assignments.length === 0) return;
    setAssigning(true);
    try {
      await ivApi.bulkAssignMapLabels(sessionId, assignments);
      await fetchMeasurements(sessionId);
      setAutoOpen(false);
      setSchemaPreview(null);
    } catch (err) {
      setError(String(err));
    } finally {
      setAssigning(false);
    }
  };

  // ── Detect segments from sample ────────────────────────────────────────────

  const handleDetectM1 = () => {
    setM1Segments(detectMarkerTokens(m1Sample.trim()));
    setSchemaPreview(null);
  };

  const handleDetectM2 = () => {
    setM2Segments(detectMarkerTokens(m2Sample.trim()));
    setSchemaPreview(null);
  };

  const handleDetectM3 = () => {
    setM3Segments(detectMarkerTokens(m3Sample.trim()));
    setSchemaPreview(null);
  };

  // ── Unassign selected ─────────────────────────────────────────────────────

  const handleUnassign = async () => {
    if (selected.size === 0 || assigning) return;
    setError('');
    // F-01D-2: clear BOTH map_label and layout_id together so the row goes
    // back to a fully-unassigned state. Sending one without the other would
    // leave the row in an inconsistent intermediate state.
    const assignments = Array.from(selected).map((id) => ({
      id,
      map_label: null as null,
      layout_id: null as null,
    }));
    setAssigning(true);
    try {
      await ivApi.bulkAssignMapLabels(sessionId, assignments);
      await fetchMeasurements(sessionId);
      setSelected(new Set());
    } catch (err) {
      setError(String(err));
    } finally {
      setAssigning(false);
    }
  };

  const selectedHaveAssignments = sortedMeasurements
    .filter((m) => selected.has(m.id))
    .some((m) => m.map_label);

  const assignedCount = measurements.filter((m) => m.map_label).length;
  const ivSessionVisibility = useMapStore((s) => s.ivSessionVisibility);
  const ivLayerVisible = useMapStore((s) => s.ivLayerVisible);
  const setIVSessionVisibility = useMapStore((s) => s.setIVSessionVisibility);
  const loadSessionMeasurements = useIVStore((s) => s.loadSessionMeasurements);
  // "Shown on map" = layer switch on AND this session selected. "Show"
  // selects the session (which also switches the layer on); "Hide" only
  // deselects this session and leaves the layer + other sessions alone.
  const ivResultsVisible = ivLayerVisible && (ivSessionVisibility[sessionId] ?? false);

  // ── Render helpers ─────────────────────────────────────────────────────────

  /** Render token chips for a given marker with editable field selects */
  function renderTokenChips(
    segments:    MarkerSegment[],
    setSegments: (segs: MarkerSegment[]) => void,
  ) {
    return (
      <div className="iv-assign__schema-tokens">
        {segments.map((seg, i) => (
          <span key={i} className="iv-assign__schema-token-group">
            <span className={`iv-assign__schema-token${seg.field === 'skip' ? ' iv-assign__schema-token--skip' : ''}`}>
              <code className="iv-assign__schema-token-value">{seg.value}</code>
              <select
                className="iv-assign__schema-token-select"
                value={seg.field}
                onChange={(e) => {
                  const next = [...segments];
                  next[i] = { ...seg, field: e.target.value as keyof TopologyFields | 'skip' };
                  setSegments(next);
                  setSchemaPreview(null);
                }}
              >
                <option value="skip">(skip)</option>
                {TOPO_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </span>
            {seg.sep && <span className="iv-assign__schema-token-sep">{seg.sep}</span>}
          </span>
        ))}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // F-02E-2B: short-circuit for share visitors. All hooks above ran
  // unconditionally — this guard only changes the rendered JSX, so
  // hook order stays stable across renders.
  if (isReadOnly) {
    return (
      <div
        className="iv-assign__readonly"
        style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}
        data-testid="iv-assign-panel-readonly"
      >
        Detailed IV results aren&apos;t included in shared views yet. Contact
        the link owner for the full report.
      </div>
    );
  }

  return (
    <div className="iv-assign">
      {/* Toolbar */}
      <div className="iv-assign__toolbar">
        <span className="iv-assign__hint">
          {selected.size === 0
            ? 'Select measurements, then click a string label to assign'
            : `${selected.size} selected — click a string label to assign starting there`}
        </span>
        <span className="iv-assign__stats">{assignedCount}/{measurements.length} assigned</span>
        {assignedCount > 0 && (
          <button
            className={`iv-btn iv-btn--sm${ivResultsVisible ? ' iv-btn--active' : ''}`}
            onClick={() => {
              if (!ivResultsVisible) loadSessionMeasurements(sessionId);
              setIVSessionVisibility(sessionId, !ivResultsVisible);
            }}
            title={ivResultsVisible ? 'Hide IV results on map' : 'Show IV results on map'}
          >
            📍 {ivResultsVisible ? 'Hide on Map' : 'Show on Map'}
          </button>
        )}
        {selected.size > 0 && selectedHaveAssignments && (
          <button className="iv-btn iv-btn--sm iv-btn--danger" onClick={handleUnassign} disabled={assigning}>
            Unassign
          </button>
        )}
        <button
          className={`iv-btn iv-btn--sm${autoOpen ? ' iv-btn--active' : ''}`}
          onClick={() => { setAutoOpen((v) => !v); setSchemaPreview(null); }}
          disabled={assigning}
        >
          ⚡ Auto-assign
        </button>
      </div>

      {/* Auto-assign panel */}
      {autoOpen && (
        <div className="iv-assign__auto-panel">
          {/* Mode tabs */}
          <div className="iv-assign__auto-modes">
            <button
              className={`iv-assign__auto-mode-btn${autoMode === 'sequential' ? ' iv-assign__auto-mode-btn--active' : ''}`}
              onClick={() => { setAutoMode('sequential'); setSchemaPreview(null); }}
            >Sequential</button>
            <button
              className={`iv-assign__auto-mode-btn${autoMode === 'schema' ? ' iv-assign__auto-mode-btn--active' : ''}`}
              onClick={() => { setAutoMode('schema'); setSchemaPreview(null); }}
            >By Marker Schema</button>
          </div>

          {/* Sequential config */}
          {autoMode === 'sequential' && (
            <div className="iv-assign__auto-bar">
              <div className="iv-assign__auto-group">
                <span className="iv-assign__auto-label">Measurements</span>
                <div className="iv-assign__auto-toggle">
                  <button className={`iv-assign__auto-btn${mOrder === 'asc' ? ' iv-assign__auto-btn--active' : ''}`} onClick={() => setMOrder('asc')}>↑ Asc</button>
                  <button className={`iv-assign__auto-btn${mOrder === 'desc' ? ' iv-assign__auto-btn--active' : ''}`} onClick={() => setMOrder('desc')}>↓ Desc</button>
                </div>
              </div>
              <div className="iv-assign__auto-sep" />
              <div className="iv-assign__auto-group">
                <span className="iv-assign__auto-label">Labels</span>
                <div className="iv-assign__auto-toggle">
                  <button className={`iv-assign__auto-btn${lOrder === 'asc' ? ' iv-assign__auto-btn--active' : ''}`} onClick={() => setLOrder('asc')}>↑ Asc</button>
                  <button className={`iv-assign__auto-btn${lOrder === 'desc' ? ' iv-assign__auto-btn--active' : ''}`} onClick={() => setLOrder('desc')}>↓ Desc</button>
                </div>
              </div>
              <div className="iv-assign__auto-sep" />
              <label className="iv-assign__auto-check">
                <input type="checkbox" checked={skipAssigned} onChange={(e) => setSkipAssigned(e.target.checked)} />
                Skip assigned
              </label>
              <div className="iv-assign__auto-sep" />
              <span className="iv-assign__auto-preview">
                {seqPreview > 0 ? `${seqPreview} pair${seqPreview !== 1 ? 's' : ''} will be assigned` : 'Nothing to assign'}
              </span>
              <button className="iv-btn iv-btn--sm iv-btn--primary" onClick={handleSeqAutoAssign} disabled={assigning || seqPreview === 0}>
                Run
              </button>
            </div>
          )}

          {/* Schema config */}
          {autoMode === 'schema' && (
            <>
              <div className="iv-assign__schema-config">
                <div className="iv-assign__schema-note">
                  Paste a sample marker value — the system detects its tokens and suggests topology field
                  assignments. Adjust the dropdowns if needed. Matching is numeric: <code>11</code> matches <code>DCCB11</code>.
                </div>

                {/* Marker 1 */}
                <div className="iv-assign__schema-marker-block">
                  <div className="iv-assign__schema-marker-header">
                    <span className="iv-assign__schema-field-name">Marker 1</span>
                    {m1Hint && (
                      <span className="iv-assign__schema-sample-hint">
                        e.g. <code>{m1Hint}</code>
                      </span>
                    )}
                    {!m1Hint && (
                      <span className="iv-assign__schema-sample-hint iv-assign__schema-sample-hint--none">
                        (no data in file)
                      </span>
                    )}
                  </div>
                  <div className="iv-assign__schema-detect-row">
                    <input
                      className="iv-assign__schema-input"
                      type="text"
                      placeholder="Optional — paste a sample Marker 1 value…"
                      value={m1Sample}
                      onChange={(e) => { setM1Sample(e.target.value); setM1Segments([]); setSchemaPreview(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && m1Sample.trim() && handleDetectM1()}
                    />
                    <button
                      className="iv-btn iv-btn--sm"
                      onClick={handleDetectM1}
                      disabled={!m1Sample.trim()}
                    >
                      Detect →
                    </button>
                  </div>
                  {m1Segments.length > 0 && renderTokenChips(m1Segments, setM1Segments)}
                </div>

                {/* Marker 2 */}
                <div className="iv-assign__schema-marker-block">
                  <div className="iv-assign__schema-marker-header">
                    <span className="iv-assign__schema-field-name">Marker 2</span>
                    {m2Hint && (
                      <span className="iv-assign__schema-sample-hint">
                        e.g. <code>{m2Hint}</code>
                      </span>
                    )}
                  </div>
                  <div className="iv-assign__schema-detect-row">
                    <input
                      className="iv-assign__schema-input"
                      type="text"
                      placeholder="Paste a sample Marker 2 value…"
                      value={m2Sample}
                      onChange={(e) => { setM2Sample(e.target.value); setM2Segments([]); setSchemaPreview(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && m2Sample.trim() && handleDetectM2()}
                    />
                    <button
                      className="iv-btn iv-btn--sm"
                      onClick={handleDetectM2}
                      disabled={!m2Sample.trim()}
                    >
                      Detect →
                    </button>
                  </div>
                  {m2Segments.length > 0 && renderTokenChips(m2Segments, setM2Segments)}
                </div>

                {/* Marker 3 */}
                <div className="iv-assign__schema-marker-block">
                  <div className="iv-assign__schema-marker-header">
                    <span className="iv-assign__schema-field-name">Marker 3</span>
                    {m3Hint && (
                      <span className="iv-assign__schema-sample-hint">
                        e.g. <code>{m3Hint}</code>
                      </span>
                    )}
                    {!m3Hint && (
                      <span className="iv-assign__schema-sample-hint iv-assign__schema-sample-hint--none">
                        (no data in file)
                      </span>
                    )}
                  </div>
                  <div className="iv-assign__schema-detect-row">
                    <input
                      className="iv-assign__schema-input"
                      type="text"
                      placeholder="Optional — paste a sample Marker 3 value…"
                      value={m3Sample}
                      onChange={(e) => { setM3Sample(e.target.value); setM3Segments([]); setSchemaPreview(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && m3Sample.trim() && handleDetectM3()}
                    />
                    <button
                      className="iv-btn iv-btn--sm"
                      onClick={handleDetectM3}
                      disabled={!m3Sample.trim()}
                    >
                      Detect →
                    </button>
                  </div>
                  {m3Segments.length > 0 && renderTokenChips(m3Segments, setM3Segments)}
                </div>

                <div className="iv-assign__schema-note iv-assign__schema-note--info">
                  ℹ️ Test number indicates measurement order only — it is not a topology identifier and is not used for matching.
                </div>

                <div className="iv-assign__schema-actions">
                  <span className="iv-assign__schema-match-count">
                    {schemaMatchCount > 0
                      ? `${schemaMatchCount}/${sortedMeasurements.length} measurements matched`
                      : 'Detect tokens above to match measurements'}
                  </span>
                  <button
                    className="iv-btn iv-btn--sm"
                    onClick={handleSchemaPreview}
                    disabled={schemaMatchCount === 0}
                  >
                    Preview
                  </button>
                  <button
                    className="iv-btn iv-btn--sm iv-btn--primary"
                    onClick={handleSchemaRun}
                    disabled={assigning || schemaMatchCount === 0}
                  >
                    Run ({schemaMatchCount})
                  </button>
                </div>
              </div>

              {/* Preview table */}
              {schemaPreview && (
                <div className="iv-assign__schema-preview">
                  <div className="iv-assign__schema-preview-header">
                    <span>
                      Match Preview — {schemaPreview.filter((r) => r.matchedLabel).length} matched,{' '}
                      {schemaPreview.filter((r) => !r.matchedLabel).length} unmatched
                    </span>
                    <button className="iv-assign__schema-preview-close" onClick={() => setSchemaPreview(null)}>✕</button>
                  </div>
                  <div className="iv-assign__schema-preview-table-wrap">
                    <table className="iv-assign__schema-preview-table">
                      <thead>
                        <tr>
                          <th>Test#</th>
                          <th>Marker 1</th>
                          <th>Marker 2</th>
                          <th>Parsed Fields</th>
                          <th>Matched Label</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schemaPreview.map((r) => (
                          <tr key={r.id} className={r.matchedLabel ? '' : 'iv-assign__schema-preview-row--nomatch'}>
                            <td>{r.testNum ?? '—'}</td>
                            <td>{r.marker1}</td>
                            <td className="iv-assign__schema-preview-mono">{r.marker2 ?? '—'}</td>
                            <td className="iv-assign__schema-preview-fields">
                              {Object.entries(r.parsedFields).map(([k, v]) => (
                                <span key={k} className="iv-assign__schema-preview-field">{k}={v}</span>
                              ))}
                              {Object.keys(r.parsedFields).length === 0 && (
                                <span className="iv-assign__schema-preview-nofields">—</span>
                              )}
                            </td>
                            <td>
                              {r.matchedLabel
                                ? <span className="iv-assign__schema-preview-match">✓ {r.matchedLabel}</span>
                                : <span className="iv-assign__schema-preview-nomatch">✗ no match</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && <div className="iv-note iv-note--error">{error}</div>}

      {/* Two-column body */}
      <div className="iv-assign__body">

        {/* Left: measurements */}
        <div className="iv-assign__col iv-assign__col--measurements">
          <div className="iv-assign__col-header">
            <label className="iv-assign__col-title">
              <input
                type="checkbox"
                checked={selected.size === sortedMeasurements.length && sortedMeasurements.length > 0}
                onChange={handleSelectAll}
              />
              &nbsp;Measurements
            </label>
          </div>

          {sortedMeasurements.length > 0 && (
            <div className="iv-assign__row-header">
              <span />
              <span>Test#</span>
              <span>Marker 1</span>
              <span>Marker 2</span>
              <span>Pmax</span>
              <span>Result</span>
              <span>Assigned</span>
            </div>
          )}

          {sortedMeasurements.length === 0 ? (
            <div className="iv-assign__empty">No measurements imported yet</div>
          ) : (
            <div className="iv-assign__list">
              {sortedMeasurements.map((m, idx) => (
                <div
                  key={m.id}
                  className={`iv-assign__row${selected.has(m.id) ? ' iv-assign__row--selected' : ''}`}
                  onClick={(e) => handleCheck(m.id, idx, e.shiftKey)}
                >
                  <input
                    type="checkbox"
                    className="iv-assign__checkbox"
                    checked={selected.has(m.id)}
                    readOnly
                    onClick={(e) => { e.stopPropagation(); handleCheck(m.id, idx, e.shiftKey); }}
                  />
                  <span className="iv-assign__row-testnum" title="Test number (measurement order)">
                    {m.extras?.test_number != null ? String(m.extras.test_number) : (m.source_row_index ?? idx) + 1}
                  </span>
                  <span className="iv-assign__row-label" title={`Marker 1: ${m.label}`}>{m.label}</span>
                  <span className="iv-assign__row-marker iv-assign__row-marker--2" title={`Marker 2: ${m.marker2 ?? ''}`}>
                    {notPlaceholder(m.marker2) ? m.marker2 : '—'}
                  </span>
                  <span className="iv-assign__row-pmax" title="Pmax@STC (W)">
                    {m.power_mpp != null ? `${Math.round(m.power_mpp)}W` : '—'}
                  </span>
                  {m.result_pct !== null ? (
                    <span className="iv-assign__row-result" style={{ color: resultColor(m.result_pct) }}>
                      {m.result_pct >= 0 ? '+' : ''}{m.result_pct.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="iv-assign__row-result" style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                  {m.map_label ? (
                    <span className="iv-assign__row-assigned" title={`Assigned to ${m.map_label}`}>{m.map_label}</span>
                  ) : (
                    <span className="iv-assign__row-unassigned">unassigned</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: string labels */}
        <div className="iv-assign__col iv-assign__col--labels">
          <div className="iv-assign__col-header">
            <span className="iv-assign__col-title">String Labels</span>
            <input
              className="iv-assign__search"
              type="text"
              placeholder="Filter…"
              value={labelFilter}
              onChange={(e) => setLabelFilter(e.target.value)}
            />
          </div>

          {/* F-01D-2: gate on either array shape or legacy singleton. */}
          {(siteLayouts === undefined || siteLayouts.length === 0) && !siteLayout ? (
            <div className="iv-assign__empty">
              No vector layout loaded.<br />Import a site layout PDF first.
            </div>
          ) : stringLabels.length === 0 ? (
            <div className="iv-assign__empty">No string-level labels found in the layout(s).</div>
          ) : filteredLabels.length === 0 ? (
            <div className="iv-assign__empty">No labels match &ldquo;{labelFilter}&rdquo;</div>
          ) : (
            <div className="iv-assign__list">
              {filteredLabels.map((lbl, lblIdx) => {
                const assignedIds = getAssignmentIds(lbl);
                const pct         = getResultPct(lbl);
                const isAssigned  = assignedIds.length > 0;
                // F-01D-2: only show the layout-name disambiguator on labels
                // whose value appears in 2+ layouts. For non-colliding values
                // the layout context is unambiguous and the subtitle is noise.
                const showLayoutHint = collidingValues.has(lbl.value);
                return (
                  <div
                    key={lbl.key}
                    className={`iv-assign__label-row${selected.size > 0 && !assigning ? ' iv-assign__label-row--clickable' : ''}`}
                    onClick={() => handleLabelClick(lbl.value, lblIdx)}
                    title={selected.size > 0
                      ? `Assign ${Math.min(selected.size, filteredLabels.length - lblIdx)} measurement(s) starting here${showLayoutHint ? ` (${lbl.layoutName})` : ''}`
                      : `${lbl.value}${showLayoutHint ? ` — ${lbl.layoutName}` : ''}`}
                  >
                    <span className="iv-assign__dot" style={{ color: isAssigned ? resultColor(pct) : 'var(--text-muted)' }}>
                      {isAssigned ? '●' : '○'}
                    </span>
                    <span className="iv-assign__label-value">{lbl.value}</span>
                    {showLayoutHint && (
                      <span className="iv-assign__label-layout" style={{ marginLeft: 8, fontSize: '0.75em', opacity: 0.6 }}>
                        {lbl.layoutName}
                      </span>
                    )}
                    {isAssigned && <span className="iv-assign__label-count">{assignedIds.length}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {assigning && <div className="iv-assign__saving">Saving…</div>}
    </div>
  );
}
